// src/utils/aiLogger.js
import { logger, correlationMiddleware } from './logger';

const COMPONENT = 'ai-copilot';

export const aiLogger = {
  // Component lifecycle
  componentMount: (cid, componentName, props = {}) => {
    logger.debug(cid, `${COMPONENT}.${componentName} mounted`, props);
  },
  
  componentUpdate: (cid, componentName, updateType, data = {}) => {
    logger.trace(cid, `${COMPONENT}.${componentName} ${updateType}`, data);
  },
  
  // User interactions
  userAction: (cid, componentName, action, data = {}) => {
    logger.info(cid, `${COMPONENT}.${componentName} user ${action}`, data);
  },
  
  // AI Requests
  requestStart: (cid, endpoint, payload = {}) => {
    logger.info(cid, `${COMPONENT}.request.start`, { 
      endpoint,
      payload_preview: sanitizePayload(payload)
    });
  },
  
  requestSuccess: (cid, endpoint, response = {}) => {
    logger.info(cid, `${COMPONENT}.request.success`, { 
      endpoint,
      response_preview: sanitizeResponse(response)
    });
  },
  
  requestError: (cid, endpoint, error, context = {}) => {
    logger.error(cid, `${COMPONENT}.request.error`, { 
      endpoint,
      error_type: error.name,
      error_message: error.message,
      error_code: error.response?.status,
      ...context
    });
  },
  
  requestBlocked: (cid, reason, context = {}) => {
    logger.warn(cid, `${COMPONENT}.request.blocked`, { reason, ...context });
  },
  
  // Mode changes
  modeChange: (cid, fromMode, toMode, context = {}) => {
    logger.debug(cid, `${COMPONENT}.mode.change`, { 
      from: fromMode, 
      to: toMode,
      ...context 
    });
  },
  
  // Health checks
  healthCheck: (cid, status, details = {}) => {
    logger.debug(cid, `${COMPONENT}.health.check`, { status, ...details });
  },
  
  healthError: (cid, error, context = {}) => {
    logger.error(cid, `${COMPONENT}.health.error`, { 
      error_message: error.message,
      ...context 
    });
  }
};

// Helper to sanitize sensitive data
const sanitizePayload = (payload) => {
  if (!payload) return null;
  
  const sanitized = { ...payload };
  
  // Remove or mask sensitive fields
  if (sanitized.apiKey) sanitized.apiKey = '***';
  if (sanitized.token) sanitized.token = '***';
  if (sanitized.password) sanitized.password = '***';
  
  // Truncate long text fields
  if (sanitized.question && sanitized.question.length > 100) {
    sanitized.question = sanitized.question.substring(0, 100) + '...';
  }
  
  if (sanitized.userQuery && sanitized.userQuery.length > 100) {
    sanitized.userQuery = sanitized.userQuery.substring(0, 100) + '...';
  }
  
  if (sanitized.prompt && sanitized.prompt.length > 100) {
    sanitized.prompt = sanitized.prompt.substring(0, 100) + '...';
  }
  
  return sanitized;
};

const sanitizeResponse = (response) => {
  if (!response) return null;
  
  const sanitized = { ...response };
  
  // Truncate long AI responses
  if (sanitized.answer && sanitized.answer.length > 200) {
    sanitized.answer = sanitized.answer.substring(0, 200) + '...';
  }
  
  if (sanitized.analysis && typeof sanitized.analysis === 'string' && sanitized.analysis.length > 200) {
    sanitized.analysis = sanitized.analysis.substring(0, 200) + '...';
  }
  
  if (sanitized.response && typeof sanitized.response === 'string' && sanitized.response.length > 200) {
    sanitized.response = sanitized.response.substring(0, 200) + '...';
  }
  
  return sanitized;
};

// Export middleware for AI requests
export const aiCorrelationMiddleware = () => {
  const { options, cid } = correlationMiddleware();
  
  // Add AI-specific headers
  return {
    options: {
      ...options,
      headers: {
        ...options.headers,
        'X-AI-Client': 'trader-agent-frontend',
        'X-AI-Client-Version': process.env.REACT_APP_VERSION || '1.0.0'
      }
    },
    cid
  };
};